import { useEffect } from "react";
import { X } from "lucide-react";
import { MessageList } from "./MessageList";
import { ChatInput } from "./ChatInput";
import type { UIMessage } from "ai";
import type { ModelEntry, ChatConfig, EffortLevel } from "../shared/chat-config";
import type { PendingAttachment } from "./AttachmentPreview";
import type { AudioCache } from "../voice/audio-cache";

interface TranscriptPanelProps {
  open: boolean;
  onClose: () => void;
  messages: UIMessage[];
  isLoading: boolean;
  error?: Error;
  connected: boolean;
  onSend: (message: string, attachments?: PendingAttachment[]) => void;
  onStop: () => void;
  onStopSpeaking?: () => void;
  /** PATH B SLICE 4A — active session's companion display name, threaded to
   *  MessageList so transcript bubbles match the chat view's identity. */
  companionName?: string;
  activeSessionId?: string | null;
  config: ChatConfig;
  currentModel: ModelEntry;
  availableModels: ModelEntry[];
  onModelChange: (modelId: string) => void;
  onEffortChange: (effort: EffortLevel) => void;
  onToggleThinking: () => void;
  onSpeak?: (message: UIMessage) => void;
  speakingMessageId?: string | null;
  seekTo?: (messageId: string, fraction: number) => void;
  getPlaybackProgress?: () => number;
  audioCache?: AudioCache;
  voiceId?: string;
  playbackSpeed?: number;
  onSpeedChange?: (speed: number) => void;
}

export function TranscriptPanel({
  open,
  onClose,
  messages,
  isLoading,
  error,
  connected,
  onSend,
  onStop,
  onStopSpeaking,
  companionName,
  activeSessionId,
  config,
  currentModel,
  availableModels,
  onModelChange,
  onEffortChange,
  onToggleThinking,
  onSpeak,
  speakingMessageId,
  seekTo,
  getPlaybackProgress,
  audioCache,
  voiceId,
  playbackSpeed,
  onSpeedChange,
}: TranscriptPanelProps) {
  // Close panel on Escape when it is open
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div
      className={`transcript-panel${open ? " open" : ""}`}
      role="complementary"
      aria-label="Transcript"
    >
      <div className="transcript-panel__header">
        <span className="transcript-panel__title">TRANSCRIPT</span>
        <button
          className="btn btn-icon btn-ghost"
          onClick={onClose}
          aria-label="Close transcript"
        >
          <X size={16} />
        </button>
      </div>
      <div className="transcript-panel__body">
        <MessageList
          messages={messages}
          isLoading={isLoading}
          error={error}
          companionName={companionName}
          onSpeak={onSpeak}
          speakingMessageId={speakingMessageId}
          seekTo={seekTo}
          getPlaybackProgress={getPlaybackProgress}
          audioCache={audioCache}
          voiceId={voiceId}
          playbackSpeed={playbackSpeed}
          onSpeedChange={onSpeedChange}
          onSendMessage={onSend}
        />
      </div>
      <div className="transcript-panel__input">
        <ChatInput
          onSend={onSend}
          onStop={onStop}
          isLoading={isLoading}
          disabled={!connected}
          config={config}
          currentModel={currentModel}
          availableModels={availableModels}
          onModelChange={onModelChange}
          onEffortChange={onEffortChange}
          onToggleThinking={onToggleThinking}
          activeSessionId={activeSessionId}
          onStopSpeaking={onStopSpeaking}
          barExpanded={true}
          onToggleBar={() => undefined}
          onCollapseBar={() => undefined}
          onExpandBar={() => undefined}
          prefersReducedMotion={false}
        />
      </div>
    </div>
  );
}
